import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { DayPicker } from 'react-day-picker'
import { DATETIME_FORMAT, LOCALE_DATE_FORMAT, MINUTES_STEP } from 'commons/js/constants'
import { parseMoment, toDate, mergeDate } from 'commons/js/util/dateConverter'
import { calculateToday } from 'commons/js/services/dateCalculator'
import Input from './Input'
import { Flex } from '@elparking/components'
import TimePicker from '../TimePicker'
import { defineMessages, injectIntl } from 'react-intl'
import { LocaleMapping } from '@elparking/utils'

const messages = defineMessages({
    ariaLabelNav: {
        id: 'ariaLabelNav',
        defaultMessage: 'Cambiar año',
    },
    ariaLabelNextButton: {
        id: 'ariaLabelNextButton',
        defaultMessage: 'Selecciona el siguiente mes',
    },
    ariaLabelPreviousButton: {
        id: 'ariaLabelPreviousButton',
        defaultMessage: 'Selecciona el mes anterior',
    },
    ariaDateTimePicker: {
        id: 'ariaDateTimePicker',
        defaultMessage: 'Para abrir o cerrar el calendario, haz click sobre el campo de fecha o presiona la tecla Enter o la barra espaciadora',
    },
    inputAriaLabel: {
        id: 'inputAriaLabel',
        defaultMessage: 'Campo de fecha y hora',
    },
})

const DesktopDateTimePicker = ({
    value,
    format = LOCALE_DATE_FORMAT,
    dayPickerProps = {},
    timePickerProps = {},
    onChange = () => { },
    datePlaceholder,
    hourPlaceholder,
    minutesStep = MINUTES_STEP,
    isOpen: initialIsOpen = false,
    onFocus = () => { },
    intl,
    inputAriaLabel = intl.formatMessage(messages.inputAriaLabel),
}) => {
    const [selectedDay, setSelectedDayState] = useState(value ? moment(value, DATETIME_FORMAT) : null)
    const [month, setMonth] = useState(value ? moment(value, DATETIME_FORMAT) : null)
    const [isDayPickerOpen, setIsDayPickerOpen] = useState(initialIsOpen)

    const dayPickerInputRef = useRef(null)

    useEffect(() => {
        const newSelectedDay = value ? parseMoment(value, DATETIME_FORMAT) : null
        if (value !== selectedDay) {
            setSelectedDayState(newSelectedDay)
            setMonth(newSelectedDay)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    useEffect(() => {
        setIsDayPickerOpen(initialIsOpen)
    }, [initialIsOpen])
    const handleDayChange = (date) => {
        const m = parseMoment(date)
        updateSelectedDay({
            year: m.year(),
            month: m.month(),
            date: m.date(),
        })
        setIsDayPickerOpen(false)
        dayPickerInputRef && dayPickerInputRef.current.focus()
    }

    const handleTimeChange = (date) => {
        const m = parseMoment(date)
        updateSelectedDay({
            hours: m.hours(),
            minutes: m.minutes(),
            seconds: m.seconds(),
        })
    }

    const updateSelectedDay = (partialDate) => {
        setSelectedDayState((prevSelectedDay) => {
            const newSelectedDay = mergeDate(prevSelectedDay || toDate(calculateToday(minutesStep)), partialDate)
            onChange(newSelectedDay)
            return newSelectedDay
        })
    }
    const openModalWithAccessibility = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsDayPickerOpen(!isDayPickerOpen)
        } else if (e.key === 'Escape') {
          e.preventDefault()
          setIsDayPickerOpen(false)
        }
    }
    return (
        <Flex>
            <Flex width='60%' flexDirection='column' onKeyDown={openModalWithAccessibility}>
                <Input
                  data-test='datetime-picker-input'
                  aria-label={inputAriaLabel + '.' + intl.formatMessage(messages.ariaDateTimePicker)}
                  ref={dayPickerInputRef}
                  value={selectedDay}
                  placeholder={datePlaceholder}
                  format={format}
                  onClick={onFocus}
                  onKeyDown={openModalWithAccessibility}
                  />
                {isDayPickerOpen && (
                    <DayPicker
                      data-test='day-picker'
                      required
                      animate
                      locale={LocaleMapping[dayPickerProps.locale] || 'es'}
                      mode='single'
                      selected={selectedDay}
                      onSelect={handleDayChange}
                      month={month}
                      onMonthChange={setMonth}
                      disabled={{ before: new Date() }}
                      role='dialog'
                      aria-label={inputAriaLabel}
                      startMonth={dayPickerProps.fromMonth ? toDate(dayPickerProps.fromMonth) : null}
                      endMonth={dayPickerProps.toMonth ? toDate(dayPickerProps.toMonth) : null}
                      labels={{
                          labelNav: () => intl.formatMessage(messages.ariaLabelNav),
                          labelNext: () => intl.formatMessage(messages.ariaLabelNextButton),
                          labelPrevious: () => intl.formatMessage(messages.ariaLabelPreviousButton),
                        }}
                    />
                )}
            </Flex>
            <Flex width='40%'>
                <TimePicker
                  value={selectedDay}
                  onChange={handleTimeChange}
                  placeholder={hourPlaceholder}
                  onFocus={() => setIsDayPickerOpen(false)}
                  {...timePickerProps}
                />
            </Flex>
        </Flex>
    )
}

DesktopDateTimePicker.propTypes = {
    value: PropTypes.instanceOf(Date),
    format: PropTypes.string,
    dayPickerProps: PropTypes.object,
    timePickerProps: PropTypes.object,
    yearMonthPickerProps: PropTypes.object,
    showTimePicker: PropTypes.bool,
    showYearMonthPicker: PropTypes.bool,
    onChange: PropTypes.func,
    onNext: PropTypes.func,
    showNextButton: PropTypes.bool,
    datePlaceholder: PropTypes.string,
    hourPlaceholder: PropTypes.string,
    minutesStep: PropTypes.number,
    isOpen: PropTypes.bool,
    onFocus: PropTypes.func,
    inputAriaLabel: PropTypes.string,
    intl: PropTypes.object,
}

export default injectIntl(DesktopDateTimePicker)
